import { clsx } from 'clsx';
import { format } from 'date-fns';
import React, { useCallback, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { IconButton } from 'src/components/Buttons';
import { TagsFormBody } from 'src/components/TagsFormBody';
import { useActivePosEntityContext } from 'src/contexts/ActivePosEntityContext';
import { useAppContext } from 'src/contexts/AppContext';
import { Content } from 'src/contexts/ContentContext';
import { PosDropdown, PosDropdownItem } from 'src/core/POS/PosDropdown';
import { vars } from 'src/core/themes';
import { Button, SimpleTable, Stack } from 'src/core/ui';
import { Badge } from 'src/core/ui/Badge';
import { AddPurchasePaymentDialog } from 'src/dialogs/AddPurchasePaymentDialog';
import { ConvertCurrencyDialog } from 'src/dialogs/ConvertCurrency';
import { ConvertCurrencyForm } from 'src/dialogs/ConvertCurrency/ConvertCurrencyDialog';
import { EditPurchasePaymentDialog } from 'src/dialogs/EditPurchasePaymentDialog';
import { PurchaseCostDialog } from 'src/dialogs/PurchaseCostDialog';
import { useBasicDialog } from 'src/hooks/useBasicDialog';
import { useGetUserInfos } from 'src/hooks/userGetUserInfo';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import {
  useUserCanLockPurchaseCommission,
  useUserCanUpdatePurchaseCommission,
  useUserCanUpdatePurchaseTags,
} from 'src/hooks/useUserHasPurchasePermissions';
import {
  ListingNotesSection,
  PurchasePaymentStatusTextContainer,
} from 'src/modals/common';
import { DetailSection } from 'src/modals/common/DetailSection/DetailSection';
import { CommissionSection } from 'src/modals/common/Purchase/CommissionSection';
import { DeliverySection } from 'src/modals/common/Purchase/DeliverySection/DeliverySection';
import { recalculateUnitFullyLoadedCostPreview } from 'src/modals/common/Purchase/PurchaseMainDetails.utils';
import { PaymentStatusTextContainer } from 'src/modals/common/Purchase/PurchaseModal.styled';
import { LocationIcon, PlaceholderIcon } from 'src/svgs';
import { ConvertCurrencyIcon } from 'src/svgs/ConvertCurrencyIcon';
import {
  DeleteIcon,
  EditIcon,
  IconsFill,
  MoreIcon,
  PaymentSize,
  PlusIcon,
} from 'src/svgs/Viagogo';
import { CalendarOutlineIcon } from 'src/svgs/Viagogo';
import { TicketGroupWithHandlers } from 'src/tables/PurchaseTicketTable/configs/TicketGroupTableColumnsConfig';
import { ContentId } from 'src/utils/constants/contentId';
import {
  PURCHASE_COST_TYPE_TO_CID,
  PURCHASE_PAYMENT_STATUS_TO_CID,
} from 'src/utils/constants/contentIdMaps';
import { newBigIntId } from 'src/utils/idUtils';
import { formatCurrency } from 'src/utils/numberFormatter';
import { getPaymentMethodIcon } from 'src/utils/paymentMethodUtils';
import { posChangedField } from 'src/utils/posFieldUtils';
import { PurchaseOrderDetailsInput } from 'src/utils/purchaseUtils';
import { getDeliveryTypeIcon } from 'src/utils/ticketTypeUtils';
import { getCosts, toSeating } from 'src/utils/ticketUtils';
import {
  ActionOutboxEntityType,
  CostDetail,
  Feature,
  ListingNote,
  PurchaseOrderCostType,
  PurchaseOrderDetails,
  PurchasePayment,
  PurchasePaymentMethodType,
  PurchasePaymentStatus,
  TicketGroupInput,
} from 'src/WebApiController';

import * as styles from './EventGroupMainDetailsBody.css';

export type EventGroupMainDetailsBodyProps = {
  /** Event group name shown in the header card title. */
  eventGroupName: string;
  /** Venue name for the tickets section banner (e.g. "Yankee Stadium"). */
  venueName: string | null;
  /** Venue city + state for the tickets section banner (e.g. "Bronx NY"). */
  venueLocation: string | null;
  /** Start of the placeholder date range (ISO string). */
  startDate: string | null;
  /** End of the placeholder date range (ISO string). */
  endDate: string | null;
  /** Number of placeholder events in the group. */
  expectedEventCount: number;
  disabled?: boolean;
  allowPaymentEdits: boolean;
  allowAdditionalCostEdits: boolean;
  highlightTicketGroupIds?: number[];
  onSecondaryAccountOpen: () => void;
} & Pick<TicketGroupWithHandlers, 'onTicketGroupAction'>;

// ---------------------------------------------------------------------------
// Sub-components for table rows (hooks must be called at component level)
// ---------------------------------------------------------------------------

function TicketGroupSimpleRow({
  ticketGroup,
  currency,
}: {
  ticketGroup: TicketGroupInput;
  currency: string;
}) {
  const seating = toSeating(ticketGroup);
  const seatRange =
    seating.seatFr || seating.seatTo
      ? `${seating.seatFr ?? ''} - ${seating.seatTo ?? ''}`
      : '—';

  const deliveryType = ticketGroup.deliveryType?.value;

  const listingNotes: ListingNote[] = ticketGroup.listingNotes?.value ?? [];

  return (
    <SimpleTable.Tr className={styles.tableBodyRow}>
      <SimpleTable.Td className={clsx(styles.tableDataCell, styles.colSection)}>
        {seating.section || '—'}
      </SimpleTable.Td>
      <SimpleTable.Td className={clsx(styles.tableDataCell, styles.colSmall)}>
        {seating.row || '—'}
      </SimpleTable.Td>
      <SimpleTable.Td className={clsx(styles.tableDataCell, styles.colSmall)}>
        {seatRange}
      </SimpleTable.Td>
      <SimpleTable.Td className={clsx(styles.tableDataCell, styles.colSmall)}>
        {ticketGroup.numberOfTickets?.value ?? 0}
      </SimpleTable.Td>
      <SimpleTable.Td className={styles.tableDataCell}>
        {formatCurrency(ticketGroup.unitCost?.value, currency)}
      </SimpleTable.Td>
      {/* Type — icon only, no text */}
      <SimpleTable.Td
        className={clsx(styles.tableDataCell, styles.colIconOnly)}
      >
        <span style={{ color: vars.color.textStrong }}>
          {getDeliveryTypeIcon(deliveryType)}
        </span>
      </SimpleTable.Td>
      {/* Seat Traits */}
      <SimpleTable.Td className={styles.tableDataCell}>
        {listingNotes.length > 0 ? (
          <Stack direction="row" gap="s" style={{ flexWrap: 'wrap' }}>
            {listingNotes.map((note) => (
              <Badge key={note.listingNoteId}>{note.listingNoteText}</Badge>
            ))}
          </Stack>
        ) : null}
      </SimpleTable.Td>
    </SimpleTable.Tr>
  );
}

function PaymentMethodIcon({
  paymentMethod,
}: {
  paymentMethod: PurchasePaymentMethodType;
}) {
  const Icon = getPaymentMethodIcon(paymentMethod);
  return <Icon size={PaymentSize.s} />;
}

function PaymentStatusLabel({
  paymentStatus,
  isPaymentPastDueDate,
  isPaymentRefunded,
}: Pick<PurchasePayment, 'paymentStatus'> & {
  isPaymentPastDueDate: boolean;
  isPaymentRefunded: boolean;
}) {
  switch (paymentStatus) {
    case PurchasePaymentStatus.Paid:
      return isPaymentRefunded ? (
        <Content id={ContentId.Refunded} />
      ) : (
        <Content id={ContentId.Paid} />
      );
    case PurchasePaymentStatus.Unpaid:
      return isPaymentPastDueDate ? (
        <Content id={ContentId.Due} />
      ) : (
        <Content id={ContentId.Unpaid} />
      );
    case PurchasePaymentStatus.Failed:
      return <Content id={ContentId.Failed} />;
    case PurchasePaymentStatus.RefundNeeded:
      return <Content id={ContentId.RefundNeeded} />;
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

/**
 * Purchase-detail review for the placeholder event group flow. A header card
 * (PO summary) on top, then a standalone copy of the purchase details sections
 * (ticket groups, cost & credit, commission, payments, notes, tags). This is
 * intentionally a copy of PurchaseMainDetailsBody — not a reuse — so the ticket
 * groups section can diverge for placeholder events without affecting the normal
 * purchase flow.
 */
export const EventGroupMainDetailsBody = ({
  eventGroupName,
  venueName,
  venueLocation,
  startDate,
  endDate,
  expectedEventCount,
  disabled,
  allowPaymentEdits,
  allowAdditionalCostEdits,
  highlightTicketGroupIds,
  onSecondaryAccountOpen,
  onTicketGroupAction,
}: EventGroupMainDetailsBodyProps) => {
  const { setValue, getValues, watch } =
    useFormContext<PurchaseOrderDetailsInput>();
  const { posEntity: purchase } =
    useActivePosEntityContext<PurchaseOrderDetails>();
  const { loginContext } = useAppContext();
  const currentUserId = loginContext?.user?.userId!;

  const hasAddCostOnCancelledPurchaseFeature = useUserHasFeature(
    Feature.AllowAddCostOnCancelledPO
  );
  const hasFullyLoadedUnitCostFeature = useUserHasFeature(
    Feature.FullyLoadedUnitCost_UseStoredValue
  );
  const canEditTags = useUserCanUpdatePurchaseTags(purchase);
  const canEditCommission = useUserCanUpdatePurchaseCommission(purchase);
  const canLockUnlockCommission = useUserCanLockPurchaseCommission(purchase);

  const costDialog = useBasicDialog();
  const addPaymentDialog = useBasicDialog();
  const editPaymentDialog = useBasicDialog();
  const convertCurrencyDialog = useBasicDialog();
  const [currentCostModel, setCurrentCostModel] = useState<CostDetail>();
  const [currentPayment, setCurrentPayment] = useState<PurchasePayment>();

  const ticketGroups = watch('ticketGroups');
  const currencyCode = watch('currencyCode');
  const payments = watch('payments');
  const additionalCosts = watch('additionalCosts');
  const tags = watch('tags');
  const internalNotes = watch('privateNotes');
  const secondaryVendor = watch('secondaryVendor');
  const secondaryVendorAccount = watch('secondaryVendorAccount');
  const vendorAccount = watch('vendorAccount');
  const purchaseDate = watch('purchaseDate');
  const vendorOrderId = watch('vendorOrderId');

  const currency =
    currencyCode?.value ??
    loginContext?.user?.activeAccount?.currencyCode ??
    'USD';

  const { totalTicketCost, totalShipping, totalCost } = useMemo(
    () => getCosts(ticketGroups, additionalCosts?.value),
    [ticketGroups, additionalCosts?.value]
  );

  // Gather user info for payment conversion banners
  const convertedByUserIds = useMemo(
    () =>
      (payments?.value ?? [])
        .filter((p) => !!p.conversionUserId)
        .map((p) => p.conversionUserId) as string[],
    [payments?.value]
  );
  const userInfos = useGetUserInfos(convertedByUserIds);

  // ---------------------------------------------------------------------------
  // Payment handlers
  // ---------------------------------------------------------------------------

  const onSetPaymentPaid = useCallback(
    ({ paymentId }: PurchasePayment) => {
      const index = payments?.value?.findIndex(
        (p) => p.paymentId === paymentId
      );
      if (index != null && index >= 0) {
        const newPaymentDate = new Date().toISOString();
        if (newPaymentDate > payments!.value![index].paymentDueDate) {
          setValue(
            `payments.value.${index}.paymentStatus`,
            PurchasePaymentStatus.RefundNeeded
          );
        } else {
          setValue(
            `payments.value.${index}.paymentStatus`,
            PurchasePaymentStatus.Paid
          );
        }
        setValue(`payments.value.${index}.paymentDate`, newPaymentDate);
        setValue(`payments.hasChanged`, true);
      }
    },
    [payments, setValue]
  );

  const onEditPayment = useCallback(
    (payment: PurchasePayment) => {
      setCurrentPayment(payment);
      editPaymentDialog.launchDialog();
    },
    [editPaymentDialog]
  );

  const onEditPaymentSave = useCallback(
    (payment: PurchasePayment) => {
      const existingPayment = payments?.value?.find(
        (p) => p.paymentId === payment.paymentId
      );
      if (existingPayment) {
        payment.paymentId = existingPayment.paymentId;
        Object.assign(existingPayment, payment);
        setValue('payments', posChangedField(payments!.value));
      }
      editPaymentDialog.closeDialog();
    },
    [payments, editPaymentDialog, setValue]
  );

  const onEditPaymentCancel = useCallback(() => {
    setCurrentPayment(undefined);
    editPaymentDialog.closeDialog();
  }, [editPaymentDialog]);

  const onRemovePayment = useCallback(
    ({ paymentId }: PurchasePayment) => {
      const newPayments = payments?.value?.filter(
        (p) => p.paymentId !== paymentId
      );
      if (newPayments && newPayments?.length !== payments?.value?.length) {
        setValue('payments', posChangedField(newPayments));
      }
      editPaymentDialog.closeDialog();
    },
    [payments?.value, editPaymentDialog, setValue]
  );

  const onAddNewCost = useCallback(() => {
    setCurrentCostModel({
      id: newBigIntId(),
      cost: null,
      costType: PurchaseOrderCostType.Tax,
      date: new Date().toISOString(),
      description: null,
    } as CostDetail);
    costDialog.launchDialog();
  }, [costDialog]);

  const onAddNewCostSave = useCallback(
    (cost: CostDetail) => {
      const existingCost = additionalCosts?.value?.find(
        (p) => p.id === cost.id
      );
      if (existingCost) {
        existingCost.cost = cost.cost;
        existingCost.costType = cost.costType;
        existingCost.date = cost.date;
        existingCost.description = cost.description;
        setValue('additionalCosts', posChangedField(additionalCosts!.value));
      } else {
        setValue(
          'additionalCosts',
          posChangedField([...(additionalCosts?.value ?? []), cost])
        );
      }
      if (hasFullyLoadedUnitCostFeature) {
        recalculateUnitFullyLoadedCostPreview(setValue, getValues);
      }
      costDialog.closeDialog();
    },
    [
      additionalCosts,
      costDialog,
      hasFullyLoadedUnitCostFeature,
      setValue,
      getValues,
    ]
  );

  const onAddNewCostCancel = useCallback(() => {
    setCurrentCostModel(undefined);
    costDialog.closeDialog();
  }, [costDialog]);

  const onEditCost = useCallback(
    (cost: CostDetail) => {
      setCurrentCostModel(cost);
      costDialog.launchDialog();
    },
    [costDialog]
  );

  const onRemoveCost = useCallback(
    (costId: number) => {
      const newCosts = additionalCosts?.value?.filter((p) => p.id !== costId);
      if (newCosts && newCosts?.length !== additionalCosts?.value?.length) {
        setValue('additionalCosts', posChangedField(newCosts));
        if (hasFullyLoadedUnitCostFeature) {
          recalculateUnitFullyLoadedCostPreview(setValue, getValues);
        }
      }
    },
    [additionalCosts, hasFullyLoadedUnitCostFeature, setValue, getValues]
  );

  const onAddNewPayment = useCallback(() => {
    addPaymentDialog.launchDialog();
  }, [addPaymentDialog]);

  const onAddNewPaymentSave = useCallback(
    (newPayments: PurchasePayment[]) => {
      const existingPayments = getValues('payments');
      if (existingPayments == null) {
        setValue('payments', posChangedField(newPayments));
      } else {
        setValue(
          'payments',
          posChangedField([...existingPayments!.value!, ...newPayments])
        );
      }
      addPaymentDialog.closeDialog();
    },
    [setValue, getValues, addPaymentDialog]
  );

  const onAddNewPaymentCancel = useCallback(() => {
    addPaymentDialog.closeDialog();
  }, [addPaymentDialog]);

  const onInternalNotesChanged = useCallback(
    (notes?: string | null) =>
      setValue('privateNotes', posChangedField(notes ?? null)),
    [setValue]
  );

  const onConvertCurrency = useCallback(
    (payment: PurchasePayment) => {
      setCurrentPayment(payment);
      convertCurrencyDialog.launchDialog();
    },
    [convertCurrencyDialog]
  );

  const onCancelConvertCurrency = useCallback(() => {
    convertCurrencyDialog.closeDialog();
    setCurrentPayment(undefined);
  }, [convertCurrencyDialog]);

  const onSaveConvertCurrency = useCallback(
    (formValues: ConvertCurrencyForm) => {
      if (currentPayment && payments) {
        const updatedPayment = Object.assign({}, currentPayment, formValues);
        const filteredPayments = (payments.value ?? []).map((payment) =>
          payment.paymentId === currentPayment.paymentId
            ? updatedPayment
            : payment
        );
        setValue('payments', posChangedField(filteredPayments));
      }
      convertCurrencyDialog.closeDialog();
    },
    [convertCurrencyDialog, currentPayment, payments, setValue]
  );

  // Whether additional costs can be edited (respects cancelled PO feature flag)
  const costsDisabled = hasAddCostOnCancelledPurchaseFeature
    ? !allowAdditionalCostEdits
    : disabled;

  return (
    <Stack direction="column" gap="m">
      {/* ── Header card ── */}
      <div className={styles.headerCard}>
        <div className={styles.headerTop}>
          <div className={styles.headerName}>{eventGroupName}</div>
          {vendorOrderId?.value && (
            <div className={styles.headerOrderId}>{vendorOrderId.value}</div>
          )}
        </div>
        <div className={styles.headerInfoRow}>
          <div className={styles.headerField}>
            <span className={styles.fieldLabel}>
              <Content id={ContentId.PurchaseDate} />
            </span>
            <span className={styles.fieldValue}>
              {purchaseDate?.value
                ? new Date(purchaseDate.value).toLocaleString('en-US')
                : '—'}
            </span>
          </div>
          <span className={styles.verticalDivider} />
          <div className={styles.headerField}>
            <span className={styles.fieldLabel}>
              <Content id={ContentId.MyAccount} />
            </span>
            <span className={styles.fieldValue}>
              {vendorAccount?.value?.email ?? vendorAccount?.value?.name ?? '—'}
            </span>
          </div>
          <span className={styles.verticalDivider} />
          <div className={styles.headerField}>
            <span className={styles.fieldLabel}>
              <Content id={ContentId.PurchasedBy} />
            </span>
            <span className={styles.fieldValue}>
              {loginContext?.user?.displayName ?? '—'}
            </span>
          </div>
          <span className={styles.verticalDivider} />
          <div className={styles.headerField}>
            <span className={styles.fieldLabel}>
              <Content id={ContentId.Currency} />
            </span>
            <span className={styles.fieldValue}>{currency}</span>
          </div>
        </div>
      </div>

      {/* ── 1. Tickets Purchased ── */}
      <DetailSection
        name={<Content id={ContentId.TicketsPurchased} />}
        extraContent={
          purchase?.pmtStat ? (
            <PurchasePaymentStatusTextContainer className={purchase.pmtStat}>
              <Content id={PURCHASE_PAYMENT_STATUS_TO_CID[purchase.pmtStat]} />
            </PurchasePaymentStatusTextContainer>
          ) : undefined
        }
      >
        <div className={styles.tableWrapper}>
          {/* ── Event group banner ── */}
          <div className={styles.eventGroupBanner}>
            <div className={styles.eventGroupBannerIcon}>
              <PlaceholderIcon
                size={vars.iconSize.xs}
                fill={IconsFill.textPrimary}
              />
            </div>
            <div className={styles.eventGroupBannerContent}>
              <div className={styles.eventGroupBannerName}>
                {eventGroupName}
              </div>
              <Stack
                direction="row"
                gap="m"
                alignItems="center"
                style={{ flexWrap: 'wrap' }}
              >
                {venueName && (
                  <Stack direction="row" gap="s" alignItems="center">
                    <LocationIcon
                      size={vars.iconSize.s}
                      fill={IconsFill.currentColor}
                    />
                    <span className={styles.eventGroupBannerVenue}>
                      {venueName}
                    </span>
                    {venueLocation && (
                      <span className={styles.eventGroupBannerMeta}>
                        {venueLocation}
                      </span>
                    )}
                  </Stack>
                )}
                {(startDate || endDate) && (
                  <>
                    <span className={styles.eventGroupBannerSep}>|</span>
                    <Stack direction="row" gap="s" alignItems="center">
                      <CalendarOutlineIcon size={vars.iconSize.s} />
                      <span className={styles.eventGroupBannerMeta}>
                        {startDate
                          ? new Date(startDate).toLocaleDateString('en-US')
                          : ''}
                        {endDate &&
                          endDate !== startDate &&
                          ` - ${new Date(endDate).toLocaleDateString('en-US')}`}
                      </span>
                    </Stack>
                  </>
                )}
                {expectedEventCount > 0 && (
                  <>
                    <span className={styles.eventGroupBannerSep}>|</span>
                    <span className={styles.eventGroupBannerMeta}>
                      {expectedEventCount} <Content id={ContentId.Events} />
                    </span>
                  </>
                )}
                {totalCost != null && (
                  <>
                    <span className={styles.eventGroupBannerSep}>|</span>
                    <span className={styles.eventGroupBannerMeta}>
                      {formatCurrency(totalCost, currency)}
                    </span>
                  </>
                )}
              </Stack>
            </div>
          </div>
          <SimpleTable.Table className={styles.tableFullWidth}>
            <SimpleTable.Thead showBottomBorder={false}>
              <SimpleTable.Tr className={styles.tableHeaderRow}>
                <SimpleTable.Th
                  className={clsx(styles.tableHeaderCell, styles.colSection)}
                >
                  <Content id={ContentId.Section} />
                </SimpleTable.Th>
                <SimpleTable.Th
                  className={clsx(styles.tableHeaderCell, styles.colSmall)}
                >
                  <Content id={ContentId.Row} />
                </SimpleTable.Th>
                <SimpleTable.Th
                  className={clsx(styles.tableHeaderCell, styles.colSmall)}
                >
                  <Content id={ContentId.Seats} />
                </SimpleTable.Th>
                <SimpleTable.Th
                  className={clsx(styles.tableHeaderCell, styles.colSmall)}
                >
                  <Content id={ContentId.Quantity} />
                </SimpleTable.Th>
                <SimpleTable.Th className={styles.tableHeaderCell}>
                  <Content id={ContentId.UnitCost} />
                </SimpleTable.Th>
                {/* Type header — intentionally empty; icon is self-explanatory */}
                <SimpleTable.Th
                  className={clsx(styles.tableHeaderCell, styles.colIconOnly)}
                />
                <SimpleTable.Th className={styles.tableHeaderCell}>
                  <Content id={ContentId.SeatTraits} />
                </SimpleTable.Th>
              </SimpleTable.Tr>
            </SimpleTable.Thead>
            <SimpleTable.Tbody>
              {(ticketGroups ?? []).map((tg) => (
                <TicketGroupSimpleRow
                  key={tg.ticketGroupId}
                  ticketGroup={tg}
                  currency={currency}
                />
              ))}
            </SimpleTable.Tbody>
          </SimpleTable.Table>
        </div>
      </DetailSection>

      {secondaryVendor && secondaryVendorAccount && (
        <DeliverySection
          disabled={disabled}
          vendor={secondaryVendor.value}
          vendorAccount={secondaryVendorAccount.value}
          onChangeClick={onSecondaryAccountOpen}
        />
      )}

      {/* ── 2. Costs and Credits ── */}
      <DetailSection name={<Content id={ContentId.CostAndCredits} />}>
        <div className={styles.tableWrapper}>
          <SimpleTable.Table className={styles.tableFullWidth}>
            <SimpleTable.Thead showBottomBorder={false}>
              <SimpleTable.Tr className={styles.tableHeaderRow}>
                <SimpleTable.Th className={styles.tableHeaderCell}>
                  <Content id={ContentId.Description} />
                </SimpleTable.Th>
                <SimpleTable.Th className={styles.tableHeaderCell}>
                  <Content id={ContentId.Amount} />
                </SimpleTable.Th>
                {!costsDisabled && (
                  <SimpleTable.Th className={styles.tableHeaderCell} />
                )}
              </SimpleTable.Tr>
            </SimpleTable.Thead>
            <SimpleTable.Tbody>
              <SimpleTable.Tr className={styles.tableBodyRow}>
                <SimpleTable.Td className={styles.tableDataCell}>
                  <Content id={ContentId.TotalTicketCost} />
                </SimpleTable.Td>
                <SimpleTable.Td className={styles.tableDataCell}>
                  {formatCurrency(totalTicketCost, currency)}
                </SimpleTable.Td>
                {!costsDisabled && (
                  <SimpleTable.Td className={styles.tableDataCell} />
                )}
              </SimpleTable.Tr>
              <SimpleTable.Tr className={styles.tableBodyRow}>
                <SimpleTable.Td className={styles.tableDataCell}>
                  <Content id={ContentId.Shipping} />
                </SimpleTable.Td>
                <SimpleTable.Td className={styles.tableDataCell}>
                  {formatCurrency(totalShipping, currency)}
                </SimpleTable.Td>
                {!costsDisabled && (
                  <SimpleTable.Td className={styles.tableDataCell} />
                )}
              </SimpleTable.Tr>
              {additionalCosts?.value?.map((cost) => (
                <SimpleTable.Tr key={cost.id} className={styles.tableBodyRow}>
                  <SimpleTable.Td
                    className={styles.tableDataCell}
                    title={cost.description ?? undefined}
                  >
                    <Content id={PURCHASE_COST_TYPE_TO_CID[cost.costType]} />
                  </SimpleTable.Td>
                  <SimpleTable.Td
                    className={styles.tableDataCell}
                    title={cost.description ?? undefined}
                  >
                    {cost.cost?.disp ||
                      formatCurrency(
                        cost.cost?.amt,
                        cost.cost?.currency ?? currency,
                        cost.cost?.dec
                      )}
                  </SimpleTable.Td>
                  {!costsDisabled && (
                    <SimpleTable.Td className={styles.tableDataCell}>
                      <PosDropdown
                        trigger={
                          <MoreIcon
                            withHoverEffect
                            fill={IconsFill.textBrand}
                            align="middle"
                          />
                        }
                        align="end"
                      >
                        <PosDropdownItem onClick={() => onEditCost(cost)}>
                          <Content id={ContentId.Edit} />
                        </PosDropdownItem>
                        <PosDropdownItem onClick={() => onRemoveCost(cost.id)}>
                          <Content id={ContentId.Remove} />
                        </PosDropdownItem>
                      </PosDropdown>
                    </SimpleTable.Td>
                  )}
                </SimpleTable.Tr>
              ))}
              {/* Total row */}
              <SimpleTable.Tr className={styles.tableBodyRow}>
                <SimpleTable.Td
                  className={clsx(styles.tableDataCell, styles.totalCostLabel)}
                >
                  <Content id={ContentId.TotalPurchaseCost} />
                </SimpleTable.Td>
                <SimpleTable.Td
                  className={clsx(styles.tableDataCell, styles.totalCostValue)}
                >
                  {formatCurrency(totalCost, currency)}
                </SimpleTable.Td>
                {!costsDisabled && (
                  <SimpleTable.Td className={styles.tableDataCell} />
                )}
              </SimpleTable.Tr>
            </SimpleTable.Tbody>
          </SimpleTable.Table>
        </div>
        {!costsDisabled && (
          <Button variant="link" size="unset" onClick={onAddNewCost}>
            <PlusIcon size={vars.iconSize.s} fill={IconsFill.currentColor} />
            <Content id={ContentId.AddLineItem} />
          </Button>
        )}
      </DetailSection>

      {/* ── 3. Buyer Commission ── */}
      <DetailSection name={<Content id={ContentId.BuyerCommission} />}>
        <CommissionSection
          disabled={disabled}
          readonly={!canEditCommission}
          canLockUnlockCommission={canLockUnlockCommission}
          hideWrapper
        />
      </DetailSection>

      {/* ── 4. Payments ── */}
      <DetailSection name={<Content id={ContentId.Payments} />}>
        <div className={styles.tableWrapper}>
          <SimpleTable.Table className={styles.tableFullWidth}>
            {payments?.value?.length ? (
              <>
                <SimpleTable.Thead showBottomBorder={false}>
                  <SimpleTable.Tr className={styles.tableHeaderRow}>
                    <SimpleTable.Th className={styles.tableHeaderCell}>
                      <Content id={ContentId.Status} />
                    </SimpleTable.Th>
                    <SimpleTable.Th className={styles.tableHeaderCell}>
                      <Content id={ContentId.Amount} />
                    </SimpleTable.Th>
                    <SimpleTable.Th className={styles.tableHeaderCell}>
                      <Content id={ContentId.ConvertedTo} />
                    </SimpleTable.Th>
                    <SimpleTable.Th className={styles.tableHeaderCell}>
                      <Content id={ContentId.ConversionRate} />
                    </SimpleTable.Th>
                    <SimpleTable.Th className={styles.tableHeaderCell}>
                      <Content id={ContentId.PaymentDate} />
                    </SimpleTable.Th>
                    <SimpleTable.Th className={styles.tableHeaderCell}>
                      <Content id={ContentId.PaymentMethod} />
                    </SimpleTable.Th>
                    {!disabled && (
                      <SimpleTable.Th className={styles.tableHeaderCell}>
                        <Content id={ContentId.Actions} />
                      </SimpleTable.Th>
                    )}
                  </SimpleTable.Tr>
                </SimpleTable.Thead>
                <SimpleTable.Tbody>
                  {payments.value.map((payment) => {
                    const isPaymentPastDueDate =
                      payment.paymentStatus !== PurchasePaymentStatus.Paid &&
                      Date.now() > new Date(payment.paymentDueDate).getTime();
                    const isPaymentRefunded =
                      payment.paymentStatus === PurchasePaymentStatus.Paid &&
                      payment.paymentAmount.amt < 0;
                    const {
                      paymentId,
                      paymentStatus,
                      paymentDueDate,
                      paymentDate,
                      paymentAmount,
                      paymentMethod,
                      convertedPaymentAmount,
                      convertedCurrencyCode,
                      conversionRate,
                      conversionDate,
                      conversionUserId,
                    } = payment;

                    const conversionUserName =
                      userInfos.data && conversionUserId
                        ? userInfos.data[conversionUserId]?.name
                        : null;

                    return (
                      <React.Fragment key={paymentId}>
                        <SimpleTable.Tr className={styles.tableBodyRow}>
                          <SimpleTable.Td className={styles.tableDataCell}>
                            <PaymentStatusTextContainer
                              purchasePaymentStatus={paymentStatus}
                              isPaymentPastDueDate={isPaymentPastDueDate}
                              isPaymentRefunded={isPaymentRefunded}
                            >
                              <PaymentStatusLabel
                                paymentStatus={paymentStatus}
                                isPaymentPastDueDate={isPaymentPastDueDate}
                                isPaymentRefunded={isPaymentRefunded}
                              />
                            </PaymentStatusTextContainer>
                          </SimpleTable.Td>
                          <SimpleTable.Td className={styles.tableDataCell}>
                            <span
                              style={{
                                visibility: isPaymentRefunded
                                  ? 'visible'
                                  : 'hidden',
                              }}
                            >
                              -
                            </span>
                            {(
                              paymentAmount.disp ||
                              formatCurrency(
                                paymentAmount.amt,
                                paymentAmount.currency ?? currency,
                                paymentAmount.dec
                              )
                            ).replace(/^-/, '')}
                          </SimpleTable.Td>
                          <SimpleTable.Td className={styles.tableDataCell}>
                            {convertedPaymentAmount?.disp != null ||
                            convertedPaymentAmount?.currency != null ||
                            convertedCurrencyCode != null
                              ? convertedPaymentAmount?.disp ||
                                formatCurrency(
                                  convertedPaymentAmount?.amt,
                                  convertedPaymentAmount?.currency ??
                                    convertedCurrencyCode,
                                  convertedPaymentAmount?.dec
                                )
                              : null}
                          </SimpleTable.Td>
                          <SimpleTable.Td className={styles.tableDataCell}>
                            {conversionRate}
                          </SimpleTable.Td>
                          <SimpleTable.Td className={styles.tableDataCell}>
                            {(paymentStatus === PurchasePaymentStatus.Paid ||
                              paymentStatus ===
                                PurchasePaymentStatus.RefundNeeded) &&
                            paymentDate
                              ? format(new Date(paymentDate), 'MMM d, yyyy')
                              : format(new Date(paymentDueDate), 'MMM d, yyyy')}
                          </SimpleTable.Td>
                          <SimpleTable.Td className={styles.tableDataCell}>
                            <Stack direction="row" gap="s" alignItems="center">
                              <PaymentMethodIcon
                                paymentMethod={paymentMethod.type}
                              />
                              {paymentStatus === PurchasePaymentStatus.Paid ||
                              paymentStatus ===
                                PurchasePaymentStatus.RefundNeeded ? (
                                paymentMethod.display
                              ) : (
                                <Button
                                  variant="link"
                                  disabled={disabled || !allowPaymentEdits}
                                  onClick={() => onSetPaymentPaid(payment)}
                                >
                                  <Content id={ContentId.SetAsPaid} />
                                </Button>
                              )}
                            </Stack>
                          </SimpleTable.Td>
                          {!disabled && (
                            <SimpleTable.Td className={styles.tableDataCell}>
                              <Stack direction="row" gap="s">
                                <IconButton
                                  icon={<ConvertCurrencyIcon withHoverEffect />}
                                  onClick={() => onConvertCurrency(payment)}
                                  titleContentId={ContentId.ConvertCurrency}
                                />
                                <IconButton
                                  icon={
                                    <EditIcon
                                      withHoverEffect
                                      fill={IconsFill.currentColor}
                                    />
                                  }
                                  onClick={() => onEditPayment(payment)}
                                  titleContentId={ContentId.Edit}
                                />
                                <IconButton
                                  icon={
                                    <DeleteIcon
                                      fill={IconsFill.textNegative}
                                      withHoverEffect
                                    />
                                  }
                                  onClick={() => onRemovePayment(payment)}
                                  titleContentId={ContentId.Remove}
                                />
                              </Stack>
                            </SimpleTable.Td>
                          )}
                        </SimpleTable.Tr>
                        {conversionUserName && conversionDate && (
                          <SimpleTable.Tr
                            key={`${paymentId}-banner`}
                            className={styles.tableBodyRow}
                          >
                            <SimpleTable.Td colSpan={7}>
                              <span className={styles.conversionBanner}>
                                <Content id={ContentId.UpdatedBy} />{' '}
                                {conversionUserName}{' '}
                                <Content id={ContentId.On} />{' '}
                                {format(
                                  new Date(conversionDate),
                                  'MMM d, yyyy'
                                )}
                              </span>
                            </SimpleTable.Td>
                          </SimpleTable.Tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </SimpleTable.Tbody>
              </>
            ) : (
              <SimpleTable.Tbody>
                <SimpleTable.Tr className={styles.tableBodyRow}>
                  <SimpleTable.Td className={styles.tableDataCell}>
                    <Content id={ContentId.None} />
                  </SimpleTable.Td>
                </SimpleTable.Tr>
              </SimpleTable.Tbody>
            )}
          </SimpleTable.Table>
        </div>
        {allowPaymentEdits && (
          <Button variant="link" onClick={onAddNewPayment}>
            <PlusIcon size={vars.iconSize.s} fill={IconsFill.currentColor} />
            <Content id={ContentId.AddLineItem} />
          </Button>
        )}
      </DetailSection>

      {/* ── Notes ── */}
      <DetailSection name={<Content id={ContentId.Notes} />}>
        <ListingNotesSection
          internalNotes={internalNotes?.value}
          disabled={disabled}
          onInternalNotesChanged={onInternalNotesChanged}
        />
      </DetailSection>

      {/* ── Tags ── */}
      <DetailSection name={<Content id={ContentId.Tags} />}>
        <TagsFormBody
          tagsCurrentEntity={tags?.value ?? []}
          onTagsUpdate={(updated) =>
            setValue('tags', { value: updated, hasChanged: true })
          }
          disabled={disabled}
          entityType={ActionOutboxEntityType.Purchase}
          canEditTags={canEditTags}
        />
      </DetailSection>

      {costDialog.dialogProps.isOpen && (
        <PurchaseCostDialog
          {...costDialog.dialogProps}
          cost={currentCostModel!}
          currencyCode={currency}
          onSave={onAddNewCostSave}
          onClosed={onAddNewCostCancel}
        />
      )}
      {addPaymentDialog.dialogProps.isOpen && (
        <AddPurchasePaymentDialog
          {...addPaymentDialog.dialogProps}
          defaultCurrencyCode={currency}
          onSave={onAddNewPaymentSave}
          onCancel={onAddNewPaymentCancel}
        />
      )}
      {editPaymentDialog.dialogProps.isOpen && currentPayment && (
        <EditPurchasePaymentDialog
          {...editPaymentDialog.dialogProps}
          onSave={onEditPaymentSave}
          onCancel={onEditPaymentCancel}
          payment={currentPayment}
        />
      )}
      {convertCurrencyDialog.dialogProps.isOpen && currentPayment && (
        <ConvertCurrencyDialog
          {...convertCurrencyDialog.dialogProps}
          onSave={onSaveConvertCurrency}
          onCancel={onCancelConvertCurrency}
          payment={currentPayment}
          currentUserId={currentUserId}
        />
      )}
    </Stack>
  );
};
